/**
 * Agent protocol types — WebSocket protocol between AgentDO (server) and clients.
 *
 * All Agent → Client events carry a monotonic `seq` number for ordering.
 */

// ── Agent → Client Events ───────────────────────────────────────────────────

export interface StatusEvent {
  type: 'status';
  seq: number;
  phase: 'thinking' | 'tool_execution' | 'idle' | 'retrying' | 'truncating';
  /** Optional human-readable message (e.g. retry reason). */
  message?: string;
  /** Consumers drop `text` and `text_delta` events with seq >= this and below this event's seq: that attempt failed and is being retried. */
  discardFromSeq?: number;
}

export interface ToolCallEvent {
  type: 'tool_call';
  seq: number;
  id: string;
  tool: string;
  input: Record<string, unknown>;
}

export interface ToolResultEvent {
  type: 'tool_result';
  seq: number;
  id: string;
  output: string;
  is_error?: boolean;
  duration_ms?: number;
}

export interface ToolOutputEvent {
  type: 'tool_output';
  seq: number;
  id: string;
  stream: 'stdout' | 'stderr';
  text: string;
}

export interface TextEvent {
  type: 'text';
  seq: number;
  content: string;
}

export interface TextDeltaEvent {
  type: 'text_delta';
  seq: number;
  text: string;
}

export interface FileChangedEvent {
  type: 'file_changed';
  seq: number;
  path: string;
  content: string;
  status: 'modified' | 'created' | 'deleted';
}

export interface DoneEvent {
  type: 'done';
  seq: number;
  success: boolean;
  exit_code?: number;
  cache_read_tokens?: number;
  cache_creation_tokens?: number;
}

export interface ErrorEvent {
  type: 'error';
  seq: number;
  message: string;
}

export interface ResultEvent {
  type: 'result';
  seq: number;
  success: boolean;
  text: string;
}

export interface PresenceUser {
  userId: string | null;
  role: 'owner' | 'editor' | 'viewer';
  avatar?: string;
}

export interface PresenceEvent {
  type: 'presence';
  seq: number;
  users: PresenceUser[];
}

export interface PingEvent {
  type: 'ping';
  seq: number;
}

export type AgentEvent =
  | StatusEvent
  | ToolCallEvent
  | ToolResultEvent
  | ToolOutputEvent
  | TextEvent
  | TextDeltaEvent
  | FileChangedEvent
  | DoneEvent
  | ErrorEvent
  | ResultEvent
  | PresenceEvent
  | PingEvent;

// ── Client → Agent Messages ─────────────────────────────────────────────────

export interface AgentPrompt {
  type: 'prompt';
  text: string;
}

export interface AgentCancel {
  type: 'cancel';
}

export interface FileSyncMessage {
  type: 'file_sync';
  path: string;
  content: string;
}

export interface ReconnectMessage {
  type: 'reconnect';
  last_seq: number;
}

export interface PongMessage {
  type: 'pong';
}

export type ClientMessage = AgentPrompt | AgentCancel | FileSyncMessage | ReconnectMessage | PongMessage;

// ── Tool Definitions ────────────────────────────────────────────────────────

export interface AgentTool {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

// ── Session ─────────────────────────────────────────────────────────────────

export type AgentSessionStatus = 'active' | 'idle' | 'completed' | 'error';

export interface AgentSession {
  id: string;
  userId: string;
  repoId?: string;
  branch?: string;
  status: AgentSessionStatus;
  createdAt: string;
  updatedAt: string;
}

export type SocketRole = 'owner' | 'editor' | 'viewer';
